"use client";

import * as React from "react";
import { useRouter } from "next/navigation";
import {
  BookOpen,
  Home,
  LayoutDashboard,
  PlayCircle,
  PlusCircle,
  Receipt,
  ScrollText,
  Sparkles,
  Wallet,
} from "lucide-react";

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";

/* ──────────────────────────────────────────────────────────────────────────
 * Global command palette (cmd+K).
 *
 * Power-user shortcut for navigation and quick actions. Linear, Stripe,
 * Mercury all ship this; it's a "feels like a serious tool" signal at
 * almost-zero implementation cost.
 *
 * v1 commands are mostly placeholders. As real features land (transaction
 * entry, year-end report generation, etc.), wire them here. Keep groups
 * tight — palette UX degrades fast past 15-20 items per group.
 *
 * Mount in `app/layout.tsx` so it's available everywhere. The component
 * binds its own keyboard listener; nothing else needs to know it exists.
 * ────────────────────────────────────────────────────────────────────────── */

export function CommandPalette() {
  const router = useRouter();
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const go = React.useCallback(
    (path: string) => {
      setOpen(false);
      router.push(path);
    },
    [router],
  );

  return (
    <CommandDialog
      open={open}
      onOpenChange={setOpen}
      title="Command palette"
      description="Quick navigation and actions. Type to filter."
    >
      <CommandInput placeholder="Type a command or search…" />
      <CommandList>
        <CommandEmpty>No matches.</CommandEmpty>

        <CommandGroup heading="Navigation">
          <CommandItem onSelect={() => go("/")}>
            <Home />
            <span>Home</span>
          </CommandItem>
          <CommandItem onSelect={() => go("/how-it-works")}>
            <BookOpen />
            <span>How it works</span>
          </CommandItem>
          <CommandItem onSelect={() => go("/simulate")}>
            <PlayCircle />
            <span>Simulate</span>
          </CommandItem>
          <CommandItem onSelect={() => go("/tracker")}>
            <Wallet />
            <span>Tracker</span>
          </CommandItem>
          <CommandItem onSelect={() => go("/disclaimer")}>
            <ScrollText />
            <span>Disclaimer</span>
          </CommandItem>
          <CommandItem onSelect={() => go("/system")}>
            <Sparkles />
            <span>Design system / component showcase</span>
            <CommandShortcut>internal</CommandShortcut>
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Quick actions (coming soon)">
          <CommandItem disabled>
            <PlusCircle />
            <span>Log a HELOC draw</span>
          </CommandItem>
          <CommandItem disabled>
            <Receipt />
            <span>Log a dividend</span>
          </CommandItem>
          <CommandItem disabled>
            <LayoutDashboard />
            <span>Generate year-end report</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
